import axios from "axios";
import { FetchError, fetchWithAuth } from "../util/fetchUtils";
import {
  always,
  any,
  concat,
  find,
  ifElse,
  map,
  mergeDeepLeft,
  mergeDeepRight,
  mergeLeft,
  partial,
  pick,
  pluck,
  splitAt,
  toLower,
} from "ramda";
import { notBlank } from "../util/ext";
import parseLinks from "parse-link-header";
import querystring from "querystring";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuthenticationCheck } from "../manage-organizations/useAuthenticationCheck";

const checkError = async (response, props) => {
  if (response.status === 404) {
    throw new FetchError(response.status, response.statusText, {
      message: `Not Found [${response.url}]`,
      errors: [
        {
          message: response.statusMessage,
          code: "error.not_found",
        },
      ],
    });
  } else if (response.status === 401 || response.status === 403) {
    throw new FetchError(response.status, response.statusText, {
      message: "Unauthorized",
      errors: [
        {
          message: response.statusMessage,
          code: "error.unauthorized",
        },
      ],
    });
  } else {
    const errorBody = response.data;
    if (errorBody && errorBody.message) {
      throw new FetchError(response.status, response.statusText, errorBody);
    } else if (errorBody && errorBody.error) {
      throw new FetchError(response.status, response.statusText, {
        message: errorBody.error,
        errors: [
          {
            message: errorBody.error,
            code: `error.bad_${props.method.toLowerCase()}`,
          },
        ],
      });
    } else {
      throw new FetchError(response.status, response.statusText, {
        message: `Unable to do ${props.method.toLowerCase()}.`,
        errors: [
          {
            message: response.statusMessage,
            code: `error.bad_${props.method.toLowerCase()}`,
          },
        ],
      });
    }
  }
};

export const doFetchWithAuth = auth => async (
  url,
  props,
  getLocation = true,
) => {
  try {
    const response = await fetchWithAuth(auth)(url, props);
    if (response.status !== 204) {
      if (response.data) {
        return response.data;
      } else {
        const location = response.headers.location;
        if (notBlank(location) && getLocation) {
          return await doGet(auth)(location);
        } else {
          return null;
        }
      }
    } else {
      return null;
    }
  } catch (error) {
    await checkError(error.response, props);
  }
};

export const doFetch = async (url, props) => {
  try {
    const response = await axios(
      url,
      mergeDeepRight({ crossDomain: true }, props),
    );
    if (response.status !== 204) {
      return response.data ? response.data : null;
    } else {
      return null;
    }
  } catch (error) {
    await checkError(error.response, props);
  }
};

export const doHeadRequest = async url => {
  const props = { method: "HEAD", crossDomain: true };
  const response = await axios(url, props);
  return response;
};

export const useHeadRequest = url => {
  const handleNotAuthenticated = useAuthenticationCheck(url);
  return useQuery(["head-request"], () => doHeadRequest(url), {
    onError: handleNotAuthenticated,
  });
};

export const doCountFetchWithAuth = auth => async (url, props) => {
  try {
    const response = await fetchWithAuth(auth)(url, props);
    if (response.status !== 204) {
      if (response.data) {
        return {
          contents: response.data,
          count: response.headers["x-total-count"]
            ? parseInt(response.headers["x-total-count"])
            : 0,
        };
      } else {
        return null;
      }
    } else {
      return null;
    }
  } catch (error) {
    await checkError(error.response, props);
  }
};

export const doHeaderFetchWithAuth = auth => async (url, props) => {
  try {
    const response = await fetchWithAuth(auth)(url, props);
    if (response.status !== 204) {
      return {
        contents: response.data ? response.data : [],
        headers: response.headers,
      };
    } else {
      return {
        headers: response.headers,
      };
    }
  } catch (error) {
    await checkError(error.response, props);
  }
};

const linkHeaders = ["link", "links"];
const getLinkHeaders = response =>
  map(link => parseLinks(response.headers[toLower(link)]), linkHeaders);
const getNext = ifElse(
  any(notBlank),
  pluck("next", find(notBlank)),
  always(null),
);
const getCount = response =>
  response.headers["x-total-count"]
    ? parseInt(response.headers["x-total-count"])
    : null;

export const doNextFetchWithAuth = auth => async (url, props) => {
  try {
    const response = await fetchWithAuth(auth)(url, props);
    if (response.status !== 204) {
      const links = getLinkHeaders(response);
      const next = getNext(links);
      if (response.data) {
        return {
          contents: response.data,
          count: getCount(response),
          next: next,
          hasMore: Boolean(next),
        };
      } else {
        return null;
      }
    } else {
      return null;
    }
  } catch (error) {
    await checkError(error.response, props);
  }
};

export const doGetAll = auth => async (url, props = {}) => {
  const callGet = async (callUrl, list) => {
    try {
      const response = await doHeaderFetchWithAuth(auth)(
        callUrl,
        mergeLeft(props, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
      );
      const nextList = concat(list, response.contents);
      const links = parseLinks(response.headers.link);
      if (links && links.next) {
        const queryParams = querystring.encode({
          offset: links.next.offset,
          limit: links.next.limit,
        });
        const nextUrl = `${url}?${queryParams}`;

        return callGet(nextUrl, nextList);
      } else {
        return nextList;
      }
    } catch (error) {
      await checkError(error.response, props);
    }
  };

  const limitUrl = `${url}?limit=100`;
  return callGet(limitUrl, []);
};

export const doGet = auth => async (url, props = {}) => {
  return doFetchWithAuth(auth)(
    url,
    mergeLeft(props, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }),
  );
};

export const doGetWithNext = auth => async (url, props = {}) => {
  return doNextFetchWithAuth(auth)(
    url,
    mergeLeft(props, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }),
  );
};

export const doDelete = auth => async (url, props = {}) => {
  return doFetchWithAuth(auth)(
    url,
    mergeLeft(props, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    }),
  );
};

export const doDeleteWithBody = auth => async (url, data, props = {}) => {
  return doFetchWithAuth(auth)(
    url,
    mergeDeepLeft(props, {
      method: "DELETE",
      data: data,
      headers: {
        "Content-Type": "application/json",
      },
    }),
  );
};

export const doGetWithCount = auth => async (url, props = {}) => {
  return doCountFetchWithAuth(auth)(
    url,
    mergeLeft(props, {
      method: "GET",
    }),
  );
};

export const doUpdate = auth => async (url, update) => {
  return doFetchWithAuth(auth)(url, {
    method: "PATCH",
    data: update,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const doPost = auth => async (url, data, props = {}) => {
  return doFetchWithAuth(auth)(
    url,
    mergeDeepLeft(props, {
      method: "POST",
      data: data,
      headers: {
        "Content-Type": "application/json",
      },
    }),
  );
};

export const doPostWithoutAuth = async (url, data, props = {}) => {
  return doFetch(
    url,
    mergeDeepLeft(props, {
      method: "POST",
      data: data,
      headers: {
        "Content-Type": "application/json",
      },
    }),
  );
};

export const doPostWithoutLocation = auth => async (url, data, props = {}) => {
  return doFetchWithAuth(auth)(
    url,
    mergeDeepLeft(props, {
      method: "POST",
      data: data,
      headers: {
        "Content-Type": "application/json",
      },
    }),
    false,
  );
};

export const doPut = auth => async (url, data, props = {}) => {
  return doFetchWithAuth(auth)(
    url,
    mergeDeepLeft(props, {
      method: "PUT",
      data: data,
      headers: {
        "Content-Type": "application/json",
      },
    }),
  );
};

export const doPutWithoutAuth = async (url, body = {}, props = {}) => {
  const origin = document.location.origin;

  return doFetch(
    url,
    mergeDeepLeft(props, {
      method: "PUT",
      data: body,
      headers: {
        Origin: origin,
      },
    }),
  );
};

export const doUpload = auth => async (url, file, props = {}) => {
  const formData = new FormData();
  const { paramName = "file", method = "PUT" } = props;
  formData.append(paramName, file);

  const origin = document.location.origin;

  return doFetchWithAuth(auth)(url, {
    method: method,
    data: formData,
    headers: {
      Origin: origin,
    },
  });
};

export const doFileUploadNoAuth = async (
  url,
  file,
  method = "PUT",
  props = {},
) => {
  const origin = document.location.origin;

  return doFetch(
    url,
    mergeDeepLeft(props, {
      method: method,
      data: file,
      headers: {
        "Content-Type": file.type,
        Origin: origin,
      },
    }),
  );
};

export const useGet = (
  key,
  url,
  enabled = true,
  refetchOnMount = false,
  otherQueryOptions,
) => {
  const handleNotAuthenticated = useAuthenticationCheck(url);
  const auth = useSelector(state => state.auth);

  return useQuery(key, ({ queryKey }) => doGet(auth)(url), {
    onError: handleNotAuthenticated,
    enabled,
    refetchOnMount,
    ...otherQueryOptions,
  });
};

export const useGetNoAuth = (
  key,
  url,
  enabled = true,
  refetchOnMount = false,
  otherQueryOptions,
) => {
  const handleNotAuthenticated = useAuthenticationCheck(url);
  return useQuery(
    key,
    ({ queryKey }) =>
      doFetch(url, {
        headers: {
          "Content-Type": "application/json",
        },
      }),
    {
      onError: handleNotAuthenticated,
      enabled,
      refetchOnMount,
      ...otherQueryOptions,
    },
  );
};

export const useManualGet = (key, url, onSuccess, { queryOptions = {} }) => {
  const queryClient = useQueryClient();
  const handleNotAuthenticated = useAuthenticationCheck(url);
  const auth = useSelector(state => state.auth);

  const handleManualGet = async () => {
    try {
      const data = await queryClient.fetchQuery(
        key,
        () => doGet(auth)(url),
        queryOptions,
      );
      onSuccess(data);
    } catch (err) {
      handleNotAuthenticated(err);
    }
  };

  return { handleManualGet };
};

export const useGetWithNext = (
  key,
  url,
  props = {},
  { enabled = true, refetchOnMount = false, ...queryOptions } = {},
) => {
  const handleNotAuthenticated = useAuthenticationCheck(url);
  const auth = useSelector(state => state.auth);

  return useQuery(key, ({ queryKey }) => doGetWithNext(auth)(url, props), {
    onError: handleNotAuthenticated,
    enabled,
    refetchOnMount,
    ...queryOptions,
  });
};

export const useGetWithCount = (
  key,
  url,
  props = {},
  // useQuery options
  { enabled = true, ...queryOptions } = {},
) => {
  const handleNotAuthenticated = useAuthenticationCheck(url);
  const auth = useSelector(state => state.auth);

  return useQuery(key, ({ queryKey }) => doGetWithCount(auth)(url, props), {
    onError: handleNotAuthenticated,
    enabled,
    ...queryOptions,
  });
};

export const useGetWithCustomQuery = (
  key,
  url,
  queryFn,
  // useQuery options
  { enabled = true, ...queryOptions } = {},
) => {
  const handleNotAuthenticated = useAuthenticationCheck(url);

  return useQuery(key, queryFn, {
    onError: handleNotAuthenticated,
    enabled,
    ...queryOptions,
  });
};

/**
 *
 * @param {Function}  mutateFn - e.g. doPut, doPost, doUpload
 * @param {Function}  urlBuilder - a function that returns a URL pointing to an API endpoint.
 *                                 Its arguments are passed to it from "resourceIds" parameter in the useMutation hook.
 *
 * @param {Object}    options
 * @param {Object}    options.props - fetch/axios props, e.g. method
 * @param {Array}     options.queriesToInvalidate - An Array of query keys to invalidate. Query keys can be strings or an Array of strings.
 *                                                  If an array is provided with more than one item, the final item in the list is assumed
 *                                                  to be an array of strings. These strings should correlate to variables passed as arguments
 *                                                  in the useMutation function. E.g., if you want to invalidate a query with key ["module", { id }],
 *                                                  Then you would pass ["module", ["id"]] to queriesToInvalidate
 * @param {Function}  option.onSuccess
 * @returns - https://tanstack.com/query/v3/docs/framework/react/reference/useMutation
 */
export const useMutate = (
  mutateFn,
  urlBuilder,
  { props = {}, queriesToInvalidate = [], onSuccess = undefined } = {},
) => {
  const auth = useSelector(state => state.auth);
  const queryClient = useQueryClient();

  return useMutation(
    ({ data, resourceIds, ...otherVariables }) => {
      const url = urlBuilder(resourceIds);
      return mutateFn(auth)(url, data, props);
    },
    {
      onSuccess: onSuccess
        ? onSuccess
        : (_data, variables) => {
            if (notBlank(queriesToInvalidate)) {
              for (const queryKey of queriesToInvalidate) {
                if (typeof queryKey === "string" || queryKey.length === 1) {
                  queryClient.invalidateQueries(queryKey);
                } else {
                  const [queryKeys, variableKeys] = splitAt(
                    queryKey.length - 1,
                    queryKey,
                  );
                  queryClient.invalidateQueries([
                    ...queryKeys,
                    pick(variableKeys, variables?.resourceIds ?? {}),
                  ]);
                }
              }
            }
          },
    },
  );
};

const createMutation = mutateFn => partial(useMutate, [mutateFn]);

export const useDelete = createMutation(doDelete);

export const usePost = createMutation(doPost);

export const usePostWithoutLocation = createMutation(doPostWithoutLocation);

export const usePut = createMutation(doPut);

export const useUpdate = createMutation(doUpdate);

export const useUpload = createMutation(doUpload);
